import { useState, useRef, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  CircularProgress,
  TextField,
  IconButton,
  Pagination,
  Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { Document, Page } from "react-pdf";
import { pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import { Add as ZoomInIcon, Remove as ZoomOutIcon } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { CommentPin } from "../V2/Comments/CommentPin";
import { setComments } from "../../store/ApplicationSlice";

// Initialize PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

function EnhancedPdfViewer ({
  open,
  onClose,
  pdfName,
  pageNumber,
  setPageNumber,
  numPages,
  setNumPages,
  dataSection,
  username,
  currentPdfLink
}) {
  const dispatch = useDispatch();
  const selectedApplicationId = useSelector(
    state => state.intakeData.selectedAppId
  );
  const comments = useSelector(state => state.intakeData.comments);
  const showComments = useSelector(state => state.intakeData.showComments);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoaded, setIsLoaded] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(true);
  const containerRef = useRef(null);
  const [searchResults, setSearchResults] = useState([]);
  const [activeSearchResult, setActiveSearchResult] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [pdfInstance, setPdfInstance] = useState(null);
  const [searchHighlight, setSearchHighlight] = useState(null);
  const [zoom, setZoom] = useState(1.0);

  // Create a local handleDoubleClick function
  const handlePageDoubleClick = e => {
    e.preventDefault();
    e.stopPropagation();

    // Find the specific page container that was clicked
    const pageContainer = e.currentTarget.closest("[data-page-id]");
    if (!pageContainer) return;

    // Get the current page's rectangle
    const rect = e.target.getBoundingClientRect();

    // Calculate position relative to the clicked page
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const sectionEl = e.target.closest("[data-section]");
    const sectionId = sectionEl?.getAttribute("data-section") || "unknown";

    // Get the page number from the clicked element's data attribute
    const currentPageNumber = parseInt(
      e.currentTarget.getAttribute("data-page-number")
    );

    const newComment = {
      id: crypto.randomUUID(),
      application_id: selectedApplicationId,
      tab: "activeTab",
      section_id: sectionId,
      text: "",
      user: {
        name: username,
        initials: username ? username.charAt(0).toUpperCase() : "",
        color: "#1976d2"
      },
      x: x,
      y: y,
      page_number: currentPageNumber,
      is_resolved: false,
      is_open: true,
      is_editing: true
    };
    dispatch(setComments([...comments, newComment]));
  };

  const handleSearchChange = async event => {
    const query = event.target.value;
    setSearchQuery(query);

    if (!query || !pdfInstance || pdfLoading) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    try {
      const results = [];
      // Search through all pages
      for (let pageNum = 1; pageNum <= numPages; pageNum++) {
        const page = await pdfInstance.getPage(pageNum);
        const textContent = await page.getTextContent();

        textContent.items.forEach(item => {
          if (item.str.toLowerCase().includes(query.toLowerCase())) {
            results.push({
              text: item.str,
              page: pageNum,
              position: item.transform
            });
          }
        });
      }
      setSearchResults(results);
      // If we found results, automatically select the first one
      if (results.length > 0) {
        setActiveSearchResult(results[0]);
      }
    } catch (error) {
      console.error("Error searching PDF:", error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Update search result click handler
  const handleSearchResultClick = result => {
    setActiveSearchResult(result);

    // Clear any existing highlight before starting
    setSearchHighlight(null);

    // First ensure we're on the right page
    setPageNumber(result.page);

    // Wait for the page change to take effect
    requestAnimationFrame(() => {
      // Find the specific page element
      const pageElement = document.querySelector(
        `[data-page-id="page-${result.page}"]`
      );
      if (pageElement && containerRef.current) {
        // Get the page dimensions after it's rendered
        const pageContent = pageElement.querySelector(".page-content-wrapper");
        if (pageContent) {
          // Scroll the page into view
          pageElement.scrollIntoView({ behavior: "smooth", block: "start" });

          // Scroll thumbnail into view
          const thumbnailsContainer = document.querySelector(
            ".thumbnail-container"
          );
          if (thumbnailsContainer) {
            const thumbnail = thumbnailsContainer.querySelector(
              `[data-thumb-id="thumb-${result.page}"]`
            );
            if (thumbnail) {
              const containerHeight = thumbnailsContainer.clientHeight;
              const thumbHeight = thumbnail.clientHeight;
              const thumbScrollTop =
                thumbnail.offsetTop - (containerHeight - thumbHeight) / 2;

              thumbnailsContainer.scrollTo({
                top: thumbScrollTop,
                behavior: "smooth"
              });
            }
          }

          // Calculate highlight position
          const pageRect = pageContent.getBoundingClientRect();
          const textLayer = pageContent.querySelector(
            ".react-pdf__Page__textContent"
          );
          const scaleFactor = pageRect.width / 612; // standard PDF width

          // Wait longer for text layer to be ready and page to be stable
          setTimeout(() => {
            // Ensure page is still mounted and we're on the correct page
            if (!pageContent.isConnected || pageNumber !== result.page) return;

            // Find the exact text position in the text layer
            const textElements = textLayer?.querySelectorAll("span");
            let foundText = false;

            if (textElements) {
              textElements.forEach(span => {
                if (span.textContent.includes(result.text) && !foundText) {
                  foundText = true;
                  const spanRect = span.getBoundingClientRect();
                  const contentRect = pageContent.getBoundingClientRect();

                  const highlightPos = {
                    position: {
                      boundingRect: {
                        x1: spanRect.left - contentRect.left,
                        y1: spanRect.top - contentRect.top,
                        width: spanRect.width,
                        height: spanRect.height
                      },
                      pageNumber: result.page
                    },
                    content: { text: result.text }
                  };

                  setSearchHighlight(highlightPos);
                }
              });
            }

            // Fallback to transform-based positioning if exact match not found
            if (!foundText) {
              const highlightPos = {
                position: {
                  boundingRect: {
                    x1: result.position[4] * scaleFactor,
                    y1: result.position[5] * scaleFactor,
                    width: Math.max(result.position[2] * scaleFactor, 50),
                    height: Math.max(result.position[3] * scaleFactor * 1.2, 20)
                  },
                  pageNumber: result.page
                },
                content: { text: result.text }
              };
              setSearchHighlight(highlightPos);
              // Remove highlight after 1 minute
              setTimeout(() => setSearchHighlight(null), 60000);
            }
          }, 100);
        }
      }
    }, 300);
  };

  const handleDocumentLoadSuccess = async ({ numPages }) => {
    setNumPages(numPages);
    setIsLoaded(true);

    try {
      const pdf = await pdfjs.getDocument(currentPdfLink)
        .promise;
      setPdfInstance(pdf);
      setPdfLoading(false);

      // Set initial page number if not already set
      if (!pageNumber) {
        setPageNumber(1);
      }

      // Initial scroll to highlight first page
      requestAnimationFrame(() => {
        const thumbnailsContainer = document.querySelector(
          ".thumbnail-container"
        );
        const firstThumbnail = thumbnailsContainer?.querySelector(
          "[data-thumb-id='thumb-1']"
        );

        if (firstThumbnail && thumbnailsContainer) {
          const containerHeight = thumbnailsContainer.clientHeight;
          const thumbHeight = firstThumbnail.clientHeight;
          const scrollTop =
            firstThumbnail.offsetTop - (containerHeight - thumbHeight) / 2;

          thumbnailsContainer.scrollTo({
            top: scrollTop,
            behavior: "smooth"
          });
        }
      });
    } catch (error) {
      console.error("Error loading PDF for search:", error);
      setPdfLoading(true);
    }
  };

  // Optimized scroll handler with debouncing and throttling
  const handleMainContentScroll = useCallback(() => {
    const mainContent = containerRef.current;
    if (!mainContent || !isLoaded) return;

    // Find which page is most visible in the viewport
    const pages = mainContent.querySelectorAll("[data-page-id]");
    let mostVisiblePage = null;
    let maxVisibleArea = 0;

    const mainContentRect = mainContent.getBoundingClientRect();
    const mainContentTop = mainContentRect.top;
    const mainContentBottom = mainContentRect.bottom;

    pages.forEach(page => {
      const rect = page.getBoundingClientRect();

      // Calculate visibility percentage of the page
      const pageTop = Math.max(rect.top, mainContentTop);
      const pageBottom = Math.min(rect.bottom, mainContentBottom);
      const visibleHeight = Math.max(0, pageBottom - pageTop);
      const visibilityPercentage = visibleHeight / rect.height;

      if (visibilityPercentage > maxVisibleArea) {
        maxVisibleArea = visibilityPercentage;
        mostVisiblePage = page;
      }
    });

    if (mostVisiblePage) {
      const pageId = mostVisiblePage.getAttribute("data-page-id");
      const pageNum = parseInt(pageId.split("-")[1]);

      // Update page number
      setPageNumber(pageNum);

      // Find and scroll the thumbnail into view
      const thumbnailsContainer = document.querySelector(
        ".thumbnail-container"
      );
      if (!thumbnailsContainer) return;

      const thumbnail = thumbnailsContainer.querySelector(
        `[data-thumb-id="thumb-${pageNum}"]`
      );
      if (!thumbnail) return;

      // Get the thumbnail container's dimensions
      const containerRect = thumbnailsContainer.getBoundingClientRect();
      const thumbRect = thumbnail.getBoundingClientRect();

      // Calculate if thumbnail is fully visible
      const isVisible =
        thumbRect.top >= containerRect.top &&
        thumbRect.bottom <= containerRect.bottom;

      if (!isVisible) {
        // Calculate exact scroll position to center the thumbnail
        const containerHeight = thumbnailsContainer.clientHeight;
        const absoluteOffset = thumbnail.offsetTop;
        const thumbHeight = thumbnail.clientHeight;

        const scrollPosition =
          absoluteOffset - (containerHeight - thumbHeight) / 2;

        thumbnailsContainer.scrollTo({
          top: scrollPosition,
          behavior: "smooth"
        });
      }
    }
  }, [setPageNumber, isLoaded]);

  // Set up scroll listener
  useEffect(() => {
    const currentRef = containerRef.current;
    if (currentRef) {
      // Add throttling to prevent too frequent updates
      let ticking = false;
      const scrollListener = () => {
        if (!ticking) {
          window.requestAnimationFrame(() => {
            handleMainContentScroll();
            ticking = false;
          });
          ticking = true;
        }
      };

      currentRef.addEventListener("scroll", scrollListener);
      return () => currentRef.removeEventListener("scroll", scrollListener);
    }
  }, [handleMainContentScroll]);

  const scrollToPage = useCallback(
    pageNum => {
      setPageNumber(pageNum);

      // Use requestAnimationFrame for better scroll timing
      requestAnimationFrame(() => {
        // Scroll main content
        const pageElement = document.querySelector(
          `[data-page-id="page-${pageNum}"]`
        );
        const thumbnailsContainer = document.querySelector(
          ".thumbnail-container"
        );
        const thumbnail = thumbnailsContainer?.querySelector(
          `[data-thumb-id="thumb-${pageNum}"]`
        );

        if (pageElement && containerRef.current) {
          // Use scrollIntoView for more reliable scrolling
          pageElement.scrollIntoView({ behavior: "smooth", block: "start" });
        }

        if (thumbnail && thumbnailsContainer) {
          // Calculate thumbnail scroll position
          const containerHeight = thumbnailsContainer.clientHeight;
          const thumbHeight = thumbnail.clientHeight;
          const scrollTop =
            thumbnail.offsetTop - (containerHeight - thumbHeight) / 2;

          // Scroll thumbnail into center
          thumbnailsContainer.scrollTo({
            top: scrollTop,
            behavior: "smooth"
          });
        }
      });
    },
    [setPageNumber]
  );

  // Update renderAllPages to add data attribute
  const renderAllPages = () =>
    Array.from(new Array(numPages), (_, index) => (
      <div
        key={`page-${index + 1}`}
        data-page-id={`page-${index + 1}`}
        id={`page-${index + 1}`}
      >
        <Box
          sx={{
            position: "relative",
            mb: 2,
            pb: 2,
            borderBottom: "2px solid rgba(255,255,255,0.1)",
            "&:last-child": {
              borderBottom: "none"
            }
          }}
        >
          {/* Page Number Label */}
          <Typography
            variant="h10"
            sx={{
              position: "sticky",
              top: 0,
              left: 0,
              backgroundColor: "rgba(22, 146, 228, 0.8)",
              color: "#fff",
              px: 2,
              py: 1,
              borderRadius: "0 0 8px 0",
              zIndex: 1000,
              display: "inline-block"
            }}
          >
            Page {index + 1}
          </Typography>
          <div
            className="relative bg-white p-6 rounded-lg shadow-lg overflow-hidden"
            style={{ isolation: "isolate" }}
          >
            <div
              className="relative"
              data-section={`${dataSection}`}
              onDoubleClick={handlePageDoubleClick}
              data-page-number={index + 1}
            >
              <div
                className="page-content-wrapper"
                style={{ position: "relative" }}
              >
                <Page
                  pageNumber={index + 1}
                  width={(containerRef.current?.offsetWidth - 80) * zoom}
                  renderTextLayer={true}
                  renderAnnotationLayer={true}
                  scale={zoom}
                />

                {/* Search highlight */}
                {searchHighlight &&
                  searchHighlight.position.pageNumber === index + 1 && (
                    <div
                      className="search-highlight"
                      style={{
                        position: "absolute",
                        left: searchHighlight.position.boundingRect.x1 + "px",
                        top: searchHighlight.position.boundingRect.y1 + "px",
                        width:
                          searchHighlight.position.boundingRect.width + "px",
                        height:
                          searchHighlight.position.boundingRect.height + "px",
                        backgroundColor: "rgba(255, 235, 59, 0.5)",
                        border: "2px solid #ffc107",
                        pointerEvents: "none",
                        zIndex: 1000
                      }}
                    />
                  )}
              </div>

              {/* Comment Pins Container */}
              <div className="absolute inset-0 pointer-events-none z-50">
                {showComments &&
                  comments
                    .filter(comment => comment.page_number === index + 1)
                    .map(comment => (
                      <CommentPin
                        key={comment.id}
                        {...comment}
                        style={{
                          position: "absolute",
                          top: `${comment.y}px`,
                          left: `${Math.min(comment.x, 550)}px`,
                          zIndex: 100,
                          pointerEvents: "auto",
                          transform:
                            comment.x > 500 ? "translateX(-100%)" : "none"
                        }}
                        comments={comments}
                        setComments={updatedComments =>
                          dispatch(setComments(updatedComments))
                        }
                      />
                    ))}
              </div>
            </div>
          </div>
        </Box>
      </div>
    ));

  const handleZoomIn = () => {
    setZoom(prevZoom => Math.min(prevZoom + 0.2, 3)); // Max zoom 300%
  };

  const handleZoomOut = () => {
    setZoom(prevZoom => Math.max(prevZoom - 0.2, 0.5)); // Min zoom 50%
  };

  const renderThumbnails = () => (
    <Document
      file={currentPdfLink}
      onLoadSuccess={handleDocumentLoadSuccess}
      loading={<CircularProgress />}
    >
      {Array.from(new Array(numPages), (_, index) => (
        <Box
          key={`thumb-${index + 1}`}
          data-thumb-id={`thumb-${index + 1}`}
          onClick={() => scrollToPage(index + 1)}
          sx={{
            p: 1,
            m: 1,
            cursor: "pointer",
            background: "#333",
            border:
              pageNumber === index + 1
                ? "2px solid #ea580c"
                : "1px solid rgba(255,255,255,0.2)",
            borderRadius: 1,
            "&:hover": {
              background: "#444",
              borderColor: pageNumber === index + 1 ? "#ea580c" : "#fff"
            },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "4px"
          }}
        >
          <Page
            pageNumber={index + 1}
            width={150}
            renderTextLayer={false}
            renderAnnotationLayer={false}
          />
          <Typography
            variant="caption"
            sx={{
              color: "#fff",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              px: 1,
              py: 0.5,
              borderRadius: "4px",
              minWidth: "30px",
              textAlign: "center"
            }}
          >
            Page {index + 1}
          </Typography>
        </Box>
      ))}
    </Document>
  );

  // Effect to scroll to initial page when document loads
  useEffect(() => {
    if (isLoaded && pageNumber) {
      scrollToPage(pageNumber);
    }
  }, [isLoaded, pageNumber, scrollToPage]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          width: "90vw",
          maxWidth: "85vw",
          maxHeight: "95vh",
          m: 0,
          borderRadius: "8px"
        }
      }}
    >
      <DialogTitle
        style={{
          padding: "12px 16px",
          borderBottom: "1px solid rgba(0,0,0,0.06)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}
      >
        <span style={{ fontWeight: 500, fontSize: "16px" }}>{pdfName}</span>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {/* Add zoom controls before search field */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              size="small"
              onClick={handleZoomOut}
              disabled={zoom <= 0.5}
            >
              <ZoomOutIcon />
            </IconButton>
            <Typography
              variant="body2"
              sx={{ minWidth: "60px", textAlign: "center" }}
            >
              {Math.round(zoom * 100)}%
            </Typography>
            <IconButton
              size="small"
              onClick={handleZoomIn}
              disabled={zoom >= 3}
            >
              <ZoomInIcon />
            </IconButton>
          </Box>
          <TextField
            size="small"
            placeholder="Search in document..."
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <SearchIcon
                  sx={{
                    color: isSearching ? "primary.main" : "action.active",
                    animation: isSearching ? "spin 1s linear infinite" : "none",
                    mr: 1
                  }}
                />
              ),
              endAdornment: (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {searchResults.length > 0 && (
                    <Typography
                      sx={{ color: "text.secondary", fontSize: "0.75rem" }}
                    >
                      {searchResults.length} results
                    </Typography>
                  )}
                  {searchQuery && (
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearchQuery("");
                        setSearchResults([]);
                        setSearchHighlight(null);
                        setActiveSearchResult(null);
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              ),
              sx: { fontSize: "14px" }
            }}
          />
          <IconButton onClick={onClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent sx={{ height: "80vh", padding: 0, display: "flex" }}>
        {/* Search Results Drawer */}
        {searchResults.length > 0 && (
          <Box
            sx={{
              position: "absolute",
              top: "64px",
              right: "20px",
              width: "300px",
              maxHeight: "400px",
              bgcolor: "background.paper",
              boxShadow: 3,
              borderRadius: 1,
              zIndex: 1000,
              overflow: "auto"
            }}
          >
            {searchResults.map((result, index) => (
              <Box
                key={index}
                sx={{
                  p: 1,
                  cursor: "pointer",
                  "&:hover": { bgcolor: "action.hover" },
                  ...(activeSearchResult === result && {
                    bgcolor: "action.selected"
                  })
                }}
                onClick={() => handleSearchResultClick(result)}
              >
                <Typography variant="body2" noWrap>
                  {result.text}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Page {result.page}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* Thumbnail Sidebar */}
        <Box
          className="thumbnail-container"
          sx={{
            width: "200px",
            borderRight: "1px solid rgba(255,255,255,0.2)",
            overflowY: "auto",
            bgcolor: "#000",
            p: 1,
            "&::-webkit-scrollbar": {
              width: "8px"
            },
            "&::-webkit-scrollbar-track": {
              background: "#333"
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#666",
              borderRadius: "4px"
            }
          }}
        >
          {isLoaded && renderThumbnails()}
        </Box>

        {/* Main Content */}
        <Box
          sx={{
            position: "relative",
            flex: 1,
            height: "100%",
            bgcolor: "#000",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden"
          }}
        >
          <div
            ref={containerRef}
            style={{
              flex: 1,
              width: "100%",
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: "20px"
            }}
          >
            <Document
              file={currentPdfLink}
              onLoadSuccess={handleDocumentLoadSuccess}
              loading={<CircularProgress />}
            >
              {renderAllPages()}
            </Document>
          </div>

          {/* Keep existing pagination for quick navigation */}
          <Box
            sx={{
              height: 40,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(0,0,0,0.8)",
              borderTop: "1px solid rgba(255,255,255,0.1)"
            }}
          >
            <Pagination
              count={numPages}
              page={pageNumber}
              onChange={(_, page) => scrollToPage(page)}
              color="primary"
              size="small"
              sx={{
                "& .MuiPaginationItem-root": {
                  color: "#fff"
                }
              }}
            />
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

// Updated animation styles
const styles = `
@keyframes pulseHighlight {
  0% { opacity: 0.3; }
  50% { opacity: 0.6; }
  100% { opacity: 0.3; }
}

.search-highlight {
  position: absolute;
  background-color: rgba(255, 235, 59, 0.5) !important;
  border: 2px solid #ffc107 !important;
  box-shadow: 0 0 8px rgba(255, 235, 59, 0.6);
  pointer-events: none;
  animation: pulseHighlight 2s infinite;
  z-index: 1000;
  mix-blend-mode: multiply;
}
`;

// Add style tag to document
const styleSheet = document.createElement("style");
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

// DEFAULT EXPORT - This is the key fix
export default EnhancedPdfViewer;
